import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotifications } from "../contexts/NotificationContext";
import useAuth from "hooks/useAuth";
import NotificationSoundManager from "utils/NotificationSoundManager";
import { useGetNotificationPreferences } from "api/notificationPreferences";

// material-ui
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Typography,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  Button,
} from "@mui/material";

// project import
import MainCard from "./MainCard";

// assets
import { BadgeCheck } from "lucide-react";
import icons from "assets/icons";
import { headerIconButtonSx, V2 } from "layout/Dashboard/Header/headerV2Tokens";

// Notification sound
import notificationSound from "assets/audio/notification.mp3";

// Get icon based on content type
// const getContentTypeIcon = (contentType) => {
//   return contentTypeIcons[contentType] || icons.alertTriangle;
// };

// Get a friendly label for content types
const getContentTypeLabel = (type) => {
  switch (type) {
    case "medication":
      return "Médicament";
    case "expert":
      return "Expert";
    case "expert_opinion":
      return "Avis médical";
    case "institution":
      return "Institution";
    case "news":
      return "Actualité";
    case "comment":
      return "Contribution";
    case "module":
      return "Module";
    default:
      return type
        ? type.charAt(0).toUpperCase() + type.slice(1)
        : "Information";
  }
};

// ==============================|| ADMIN NOTIFICATION SECTION ||============================== //

const AdminNotificationSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const { preferences: notifPrefs } = useGetNotificationPreferences();
  const {
    adminNotifications,
    adminUnreadCount,
    isAdmin,
    markAdminNotificationAsRead,
    refreshAdminNotifications,
    shouldPlaySound,
    resetShouldPlaySound,
  } = useNotifications();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [lastPathname, setLastPathname] = useState("");
  const [lastNavigationTime, setLastNavigationTime] = useState(0); // Track when we last navigated
  const [navigationTarget, setNavigationTarget] = useState(null); // Track where we're navigating to
  const buttonRef = useRef(null);

  // Keep state for counts but without localStorage persistence
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Initialize NotificationSoundManager
  useEffect(() => {
    NotificationSoundManager.initialize(notificationSound);
  }, []);

  // Update counts when adminUnreadCount changes - keep this part
  useEffect(() => {
    if (adminUnreadCount > 0) {
      setTotalUnreadCount(adminUnreadCount);
    }
  }, [adminUnreadCount]);

  // Load notifications when component mounts
  useEffect(() => {
    if (isAdmin) {
      // console.log(`[AdminNotificationSection] Initial loading of admin notifications`);
      refreshAdminNotifications();
    }
  }, [isAdmin, refreshAdminNotifications]);

  // Track location changes to manage navigation state
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastNavigation = now - lastNavigationTime;

    // If we just navigated (within the last 3 seconds)
    if (timeSinceLastNavigation < 3000) {
      // console.log(`[AdminNotificationSection] Recent navigation detected (${timeSinceLastNavigation}ms ago), suppressing sound`);
      return; // Don't reset navigating state yet
    }

    // If pathname changed but we're not explicitly navigating from a notification
    if (lastPathname && lastPathname !== location.pathname && !isNavigating) {
      // console.log('[AdminNotificationSection] Location changed outside notification click');
      // We're still navigating from somewhere else
      setIsNavigating(true);
      setLastNavigationTime(now);

      // Schedule a reset after navigation completes
      setTimeout(() => {
        if (location.pathname !== navigationTarget) {
          setIsNavigating(false);
        }
      }, 1000);
    }

    // Update the last pathname
    setLastPathname(location.pathname);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isNavigating, lastNavigationTime, navigationTarget]);

  // Watch for shouldPlaySound flag from context and play sound only when it's true
  useEffect(() => {
    if (shouldPlaySound && !isNavigating && !anchorEl) {
      const now = Date.now();
      const timeSinceLastNavigation = now - lastNavigationTime;
      const isRecentNavigation = timeSinceLastNavigation < 3000;

      // Only play sound if not recently navigated
      if (!isRecentNavigation) {

        // Play sound only if enabled in preferences (default true)
        const canPlaySound = notifPrefs?.sound_notifications !== undefined 
          ? notifPrefs.sound_notifications 
          : true;
        if (canPlaySound) {
          NotificationSoundManager.playSound();
        } else {

        }
        
        // Reset the flag after playing sound (or deciding not to)
        resetShouldPlaySound();
      } else {

        // Reset the flag even if we didn't play sound
        resetShouldPlaySound();
      }
    }
  }, [shouldPlaySound, isNavigating, anchorEl, lastNavigationTime, notifPrefs, resetShouldPlaySound]);

  // Handle notification click - improved to prevent sound on navigation
  const handleNotificationClick = (notification) => {
    // console.log(`[AdminNotificationSection] Admin clicked on notification:`, notification);

    // Mark as read
    if (!notification.is_read) {
      // console.log(`[AdminNotificationSection] Marking notification ${notification.id} as read`);
      markAdminNotificationAsRead(notification.id);
    }

    // Set isNavigating to true to prevent sound from playing during navigation
    setIsNavigating(true);
    setLastNavigationTime(Date.now());

    // Close the panel
    handleClose();

    // Store navigation target and navigate
    let targetUrl = null;

    // Handle navigation based on notification type
    if (
      notification.notification_type === "REPORT" &&
      notification.data?.report_id
    ) {
      targetUrl = `/admin/reports/${notification.data.report_id}`;
      // console.log(`[AdminNotificationSection] Navigating admin to report details: ${targetUrl}`);
      setNavigationTarget(targetUrl);
      navigate(targetUrl);
    }
    // Handle comment notifications - using contributions path instead of comments
    else if (
      notification.notification_type === "COMMENT" &&
      (notification.data?.comment_id || notification.data?.id)
    ) {
      const commentId = notification.data?.comment_id || notification.data?.id;
      targetUrl = `/admin/notifications`;
      // console.log(`[AdminNotificationSection] Navigating admin to contribution: ${targetUrl}`);
      setNavigationTarget(targetUrl);
      navigate(targetUrl);
    }
    // Suggestions de contribution (file d'attente modération ou retour utilisateur)
    else if (
      notification.related_object?.type === "contribuer" &&
      notification.related_object.kind
    ) {
      const ro = notification.related_object;
      const kindToTab = {
        formation: 0,
        expert: 1,
        institution: 2,
        event: 3,
        document: 4,
        question: 5,
      };
      if (notification.notification_type === "CONTRIBUTION_SUBMITTED") {
        targetUrl = "/admin/notifications?tab=1";
      } else if (notification.notification_type === "CONTRIBUTION_DECISION") {
        const tab = kindToTab[ro.kind] ?? 0;
        targetUrl = `/contribuer?tab=${tab}&inner=1`;
      }
      if (targetUrl) {
        setNavigationTarget(targetUrl);
        navigate(targetUrl);
      } else {
        setTimeout(() => {
          setIsNavigating(false);
        }, 500);
      }
    }
    // Legacy support for old navigation
    else if (
      notification.data &&
      notification.data.content_type === "avis" &&
      notification.data.content_id
    ) {
      targetUrl = `/avis/${notification.data.content_id}?admin_review=true`;
      // console.log(`[AdminNotificationSection] Navigating admin to reported content: ${targetUrl}`);
      setNavigationTarget(targetUrl);
      navigate(targetUrl);
    } else {
      // console.log(`[AdminNotificationSection] No navigation target found for notification:`, notification);
      // If we're not navigating, reset the isNavigating flag
      setTimeout(() => {
        setIsNavigating(false);
      }, 500);
    }
  };

  const handleOpen = (event) => {
    // console.log(`[AdminNotificationSection] Opening notification panel`);
    setAnchorEl(event.currentTarget);
    refreshAdminNotifications();
  };

  const handleClose = () => {
    // console.log('[AdminNotificationSection] Closing notification panel');
    setAnchorEl(null);
  };

  // Reset isNavigating flag after a timeout
  // This ensures we don't get stuck in navigating mode
  useEffect(() => {
    let timer;
    if (isNavigating) {
      // Reset the navigating state after a delay to ensure we're done with the navigation
      timer = setTimeout(() => {
        // console.log('[AdminNotificationSection] Resetting navigation state after timeout');
        setIsNavigating(false);
      }, 5000); // A 5 second timeout to be absolutely sure
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isNavigating]);

  // Filter notifications by type - return all notifications
  const getFilteredNotifications = () => {
    return adminNotifications;
  };

  // Format relative time for notifications
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return "À l'instant";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Il y a ${minutes} minute${minutes > 1 ? "s" : ""}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Il y a ${hours} heure${hours > 1 ? "s" : ""}`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `Il y a ${days} jour${days > 1 ? "s" : ""}`;
    }
  };

  // Get the full profile image URL
  const getFullProfileImageUrl = (profileImagePath) => {
    if (!profileImagePath) return null;
    const baseUrl = import.meta.env.VITE_APP_API_URL.slice(0, -1);
    return profileImagePath.startsWith("http")
      ? profileImagePath
      : `${baseUrl}${profileImagePath}`;
  };

  // Truncate text with ellipsis if it's too long
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Get notification color based on type
  const getNotificationColor = (notification) => {
    if (
      notification.notification_type === "REPORT" ||
      (notification.data && notification.data.report_id)
    ) {
      return {
        light: "error.lighter",
        main: "error.main",
        dark: "error.main",
        bgHover: "error.light",
      };
    } else if (
      notification.notification_type === "COMMENT" ||
      (notification.data && notification.data.content_type === "comment")
    ) {
      return {
        light: "info.lighter",
        main: "info.main",
        dark: "info.main",
        bgHover: "info.light",
      };
    } else {
      return {
        light: "primary.lighter",
        main: "primary.main",
        dark: "primary.dark",
        bgHover: "primary.light",
      };
    }
  };

  // Don't render for non-admin users
  if (!isAdmin) {
    // console.log('[AdminNotificationSection] Not rendering - user is not an admin');
    return null;
  }

  const open = Boolean(anchorEl);
  const filteredNotifications = getFilteredNotifications();

  // console.log(`[AdminNotificationSection] Filtered notifications:`, filteredNotifications);

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: 0.5,
        position: "relative",
      }}
    >
      <Tooltip title="Notifications administrateur">
        <IconButton
          ref={buttonRef}
          onClick={handleOpen}
          color="secondary"
          variant="text"
          aria-label="ouvrir les notifications administrateur"
          size="large"
          sx={headerIconButtonSx(open)}
        >
          <Badge
            badgeContent={totalUnreadCount}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                top: 2,
                right: 4,
              },
            }}
          >
            <img
              src={icons.notifs}
              alt="Notifications administrateur"
              width="20"
              height="20"
            />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Connection status indicator */}
      <Tooltip
        title={
          isAdmin
            ? totalUnreadCount > 0
              ? "Notifications d'administration disponibles"
              : "Aucune notification d'administration"
            : "Déconnecté du système de notifications d'administration"
        }
      >
        <Box
          sx={{
            position: "absolute",
            width: 8,
            height: 8,
            bgcolor: isAdmin
              ? totalUnreadCount > 0
                ? "success.main"
                : "grey.400"
              : "error.main",
            borderRadius: "50%",
            border: "1px solid",
            borderColor: "background.paper",
            top: 8,
            right: 8,
          }}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: isSmallDevice ? "75%" : 380,
            minWidth: 320,
            maxWidth: 380,
            borderRadius: 'var(--brand-radius-md)',
            m: 0,
            p: 0,
            mt: 0.75,
            boxShadow: "0 12px 40px rgba(15, 23, 32, 0.12)",
            border: `1px solid ${V2.line}`,
            bgcolor: V2.surfaceSolid,
            zIndex: 1300,
            overflow: "hidden",
            fontFamily: V2.fontBody,
          },
        }}
        disableScrollLock={true}
      >
        <MainCard
          border={false}
          content={false}
          elevation={0}
          sx={{ width: "100%", p: 0, borderRadius: 'var(--brand-radius-md)', bgcolor: V2.surfaceSolid }}
        >
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h5"
              sx={{
                color: V2.ink,
                fontFamily: V2.fontDisplay,
                fontWeight: 600,
                fontSize: "1.05rem",
              }}
            >
              Notifications
            </Typography>
          </Box>

          <Divider />

          <Box
            sx={{
              height: "100%",
              maxHeight: 400,
              overflowX: "hidden",
              overflowY: "auto",
              p: 1,
            }}
          >
            {filteredNotifications.length > 0 ? (
              <List
                sx={{
                  width: "100%",
                  maxHeight: 400,
                  py: 0,
                }}
              >
                {filteredNotifications.map((notification) => {
                  const notifColor = getNotificationColor(notification);
                  const isReport =
                    notification.notification_type === "REPORT" ||
                    (notification.data && notification.data.report_id);
                  const isComment =
                    notification.notification_type === "COMMENT" ||
                    (notification.data &&
                      notification.data.content_type === "comment");

                  const user = notification.data?.user || {};
                  const reporter = notification.data?.reporter || {};
                  const contentObject = notification.data?.content_object || {};

                  return (
                    <Box key={notification.id}>
                      <ListItemButton
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          mb: 0.5,
                          py: 1.5,
                          px: 1.5,
                          borderRadius: 'var(--brand-radius-md)',
                          border: `1px solid ${
                            notification.is_read ? "transparent" : V2.accentBorder
                          }`,
                          bgcolor: notification.is_read
                            ? "transparent"
                            : notifColor.light || V2.accentSoft,
                          "&:hover": {
                            bgcolor: notification.is_read
                              ? V2.accentSoft
                              : notifColor.bgHover || V2.accentSoft,
                            borderColor: V2.accentBorder,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            gap: 1,
                          }}
                        >
                          {/* Row 1: User info and timing */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                src={getFullProfileImageUrl(
                                  isReport
                                    ? reporter?.profile_image
                                    : user?.profile_image_url,
                                )}
                                alt={
                                  isReport
                                    ? reporter?.username
                                    : user?.full_name
                                }
                                sx={{ width: 32, height: 32 }}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  {isReport
                                    ? reporter?.username
                                    : user?.full_name || "Utilisateur"}
                                </Typography>
                                {!isReport && user?.is_verified && (
                                  <BadgeCheck
                                    color="white"
                                    fill="#4680FE"
                                    size={16}
                                    strokeWidth={1.3}
                                  />
                                )}
                                {!isReport && user?.title && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    • {user.title}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatRelativeTime(notification.created_at)}
                            </Typography>
                          </Box>

                          {/* Row 2: Content title and type */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                textTransform: "uppercase",
                                maxWidth: "60%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {truncateText(
                                contentObject?.title || notification?.title,
                                40,
                              )}
                            </Typography>

                            <Chip
                              label={
                                isReport
                                  ? notification.data?.report_type ||
                                    "Signalement"
                                  : getContentTypeLabel(
                                      notification.data?.content_type,
                                    )
                              }
                              size="small"
                              sx={{
                                height: 22,
                                bgcolor: isReport ? "#ffebee" : "#e3f2fd",
                                color: isReport ? "#d32f2f" : "#1976d2",
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                maxWidth: "40%",
                              }}
                            />
                          </Box>

                          {/* Row 3: Comment content (only for comments) */}
                          {isComment && notification.data?.content && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {truncateText(notification.data.content, 100)}
                            </Typography>
                          )}
                        </Box>
                      </ListItemButton>
                      {/* <Divider /> */}
                    </Box>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Aucune notification
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate("/admin/notifications")}
                  startIcon={
                    <img
                      src={icons.notifs}
                      alt="Notifications"
                      width="18"
                      height="18"
                    />
                  }
                >
                  Centre de notifications
                </Button>
              </Box>
            )}
          </Box>
        </MainCard>
      </Popover>
    </Box>
  );
};

export default AdminNotificationSection;
